// MicButton.jsx — Botão de voz reusable pros composers.
//
// Electron uses OpenAI Realtime over WebRTC.
// • Safari/PWA: Web Speech API nativo (grátis, real-time streaming).
// • Electron desktop: microphone → WebRTC → gpt-live-transcribe deltas.
//
// Estados visuais:
//   • idle       — Mic + "Falar"
//   • listening  — MicOff + "Parar" + waveform + halo pulsing emerald
//   • busy       — Loader + "Transcrevendo" (só Whisper)
//   • error      — Mic + tooltip da causa
//
// Toggle: click → start. Click novamente OU Esc → stop.

import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, MicOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpeechToText } from '../hooks/useSpeechToText.js';
import { useWhisperRecorder, isElectron } from '../hooks/useWhisperRecorder.js';

const SPRING = { type: 'spring', stiffness: 380, damping: 28, mass: 0.6 };

function Waveform({ active }) {
  return (
    <span aria-hidden="true" className="inline-flex items-center gap-[2px]" style={{ height: 12 }}>
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="block w-[2px] rounded-full bg-emerald-400"
          animate={
            active
              ? { height: [4, 10, 5, 12, 6], opacity: [0.6, 1, 0.8, 1, 0.7] }
              : { height: 3, opacity: 0.3 }
          }
          transition={{
            duration: 0.9,
            repeat: active ? Infinity : 0,
            delay: i * 0.12,
            ease: 'easeInOut',
          }}
          style={{ boxShadow: active ? '0 0 6px rgba(52,211,153,0.6)' : 'none' }}
        />
      ))}
    </span>
  );
}

function errMessage(err) {
  if (!err) return null;
  const map = {
    'not-allowed': 'Permissão de microfone negada — habilite em System Settings',
    'service-not-allowed': 'Serviço de voz bloqueado',
    network: 'Voz não disponível neste navegador',
    'audio-capture': 'Microfone não encontrado',
    'no-speech': 'Não detectei voz — tente de novo',
    'not-supported': 'Seu navegador não suporta voz',
    OPENAI_NOT_CONNECTED: 'Configure o adapter OpenAI em Integrações',
    'empty-audio': 'Áudio vazio — fale mais alto',
    'language-not-supported': 'Idioma não suportado',
  };
  return map[err] || `Erro: ${err}`;
}

export default function MicButton({
  onTranscript,
  className = '',
  size = 'sm',
  lang,
  // (Pedro 2026-07-16) — ícone redondo no cluster do composer (par do rx-ctxu-btn),
  // sem label/waveform; o mic saiu do menu "+" e virou 1º cidadão da strip.
  iconOnly = false,
  // Atalho global ⌘D (Cmd/Ctrl+D) pra togglar a gravação — passar SÓ na instância
  // única do composer ativo; duas instâncias com shortcut = toggle duplo.
  shortcut = false,
}) {
  // Detecta uma vez no mount — Electron usa OpenAI Realtime, browser usa Web Speech.
  const [useWhisper] = useState(() => isElectron());

  // Web Speech (browser/PWA path)
  const speech = useSpeechToText({ lang, continuous: true, smartFormatting: true });
  // OpenAI Realtime (Electron path; hook name kept for API compatibility)
  const whisper = useWhisperRecorder({ onTranscript, language: (lang || 'pt-BR').slice(0, 2) });

  // Pega APIs do modo ativo
  const active = useWhisper ? whisper : speech;
  const { listening, error, start, stop } = active;
  const busy = useWhisper ? whisper.busy : false;
  const connecting = useWhisper ? whisper.connecting : false;
  const supported = useWhisper ? whisper.supported : speech.supported;

  // Stream final transcript do Web Speech pro composer
  const lastSentRef = useRef('');
  useEffect(() => {
    if (useWhisper) return; // Realtime já emite deltas via onTranscript
    const t = speech.transcript;
    if (t && t !== lastSentRef.current) {
      const delta = t.slice(lastSentRef.current.length);
      lastSentRef.current = t;
      if (delta.trim()) onTranscript?.(delta);
    }
  }, [useWhisper, speech.transcript, onTranscript]);

  // Esc desliga
  useEffect(() => {
    if (!listening) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        stop();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [listening, stop]);

  const handleClick = () => {
    if (busy || connecting) return;
    if (listening) {
      stop();
    } else {
      if (!useWhisper) {
        lastSentRef.current = '';
        speech.reset();
      }
      start();
    }
  };

  // ⌘D toggla a gravação (funciona até com o foco no textarea — é o ponto).
  // Ref evita re-registrar o listener a cada mudança de estado do hook de voz.
  const toggleRef = useRef(handleClick);
  toggleRef.current = handleClick;
  useEffect(() => {
    if (!shortcut) return undefined;
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        toggleRef.current();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcut]);

  if (!supported) return null;

  const dim = size === 'sm' ? 11 : 13;
  const errText = errMessage(error);

  let label = 'Falar';
  if (connecting) label = 'Conectando';
  else if (busy) label = 'Finalizando';
  else if (listening) label = 'Parar';

  let titleText = useWhisper ? 'Falar (transcrição em tempo real)' : 'Falar — pt-BR';
  if (connecting) titleText = 'Conectando transcrição em tempo real…';
  else if (busy) titleText = 'Finalizando transcrição…';
  else if (listening) titleText = 'Clique pra parar (ou Esc)';
  else if (errText) titleText = errText;
  if (shortcut && !busy && !connecting && !errText) titleText += ' — ⌘D';

  const ariaLabel = connecting
    ? 'Conectando transcrição de voz'
    : busy
      ? 'Transcrevendo áudio'
      : listening
        ? 'Parar gravação de voz'
        : 'Iniciar gravação de voz';

  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={listening}
        disabled={busy || connecting}
        aria-label={ariaLabel}
        title={titleText}
        className={
          'rx-ctxu-btn relative' +
          (listening
            ? ' !border-emerald-500/60 !bg-emerald-500/15 !text-emerald-300'
            : busy || connecting
              ? ' cursor-wait !border-apolo-accent/40 !text-apolo-accent'
              : errText
                ? ' !border-apolo-error/50 !text-apolo-error'
                : '') +
          (className ? ` ${className}` : '')
        }
      >
        {listening && !errText && !busy && (
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-full"
            style={{
              boxShadow:
                '0 0 0 1px rgba(52,211,153,0.45), 0 0 14px rgba(52,211,153,0.45), 0 0 28px rgba(52,211,153,0.22)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
        {busy || connecting ? (
          <Loader2 size={14} className="animate-spin" aria-hidden="true" />
        ) : listening ? (
          <MicOff size={14} aria-hidden="true" />
        ) : (
          <Mic size={14} aria-hidden="true" />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={listening}
      disabled={busy || connecting}
      aria-label={
        connecting
          ? 'Conectando transcrição de voz'
          : busy
            ? 'Transcrevendo áudio'
            : listening
              ? 'Parar gravação de voz'
              : 'Iniciar gravação de voz'
      }
      title={titleText}
      className={
        'relative inline-flex shrink-0 items-center gap-1 rounded-md border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing ' +
        (listening
          ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200 dark:text-emerald-200 hover:bg-emerald-500/25 [.light_&]:text-emerald-700'
          : busy || connecting
            ? 'cursor-wait border-apolo-accent/40 bg-apolo-accent/10 text-apolo-accent'
            : errText
              ? 'border-apolo-error/40 bg-apolo-errorBg text-apolo-error hover:border-apolo-error/60'
              : 'border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary hover:border-apolo-borderStrong hover:bg-apolo-surface3 hover:text-apolo-textPrimary') +
        ' ' +
        (size === 'sm' ? 'h-11 px-2 lg:h-auto lg:px-2 lg:py-1' : 'h-11 px-3 lg:h-auto lg:py-1.5') +
        ' ' +
        className
      }
    >
      {/* Halo verde SÓ durante listening ativo SEM erro/busy. Sprint v9.17 —
       * Pedro reportou halo verde piscando mesmo após error. Defesa tripla:
       * listening && !errText && !busy. AnimatePresence removido — ele
       * fazia exit animation que persistia frames extras quando o estado
       * oscilava rápido (start → error → stop). Render condicional puro
       * some imediato sem transição. */}
      {listening && !errText && !busy && (
        <motion.span
          aria-hidden="true"
          className="absolute inset-0 rounded-md"
          style={{
            boxShadow:
              '0 0 0 1px rgba(52,211,153,0.45), 0 0 14px rgba(52,211,153,0.45), 0 0 28px rgba(52,211,153,0.22)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      <span className="relative z-10 inline-flex items-center gap-1">
        {busy || connecting ? (
          <Loader2 size={dim} className="animate-spin" aria-hidden="true" />
        ) : listening ? (
          <MicOff size={dim} aria-hidden="true" />
        ) : (
          <Mic size={dim} aria-hidden="true" />
        )}
        <span className="text-[11px] font-medium">{label}</span>
        <AnimatePresence mode="wait">
          {listening ? (
            <motion.span
              key="wave"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              transition={SPRING}
            >
              <Waveform active />
            </motion.span>
          ) : null}
        </AnimatePresence>
      </span>
    </button>
  );
}
